/* eslint-disable complexity */
AtcFiltersService.SERVICE_NAME = 'atcFlightFiltersService';

export default function AtcFiltersService(
    timeService,
    atcFFactory,
) {
    'ngInject';

    Object.assign(this, {
        getProcessedFilters,
        sortAirlines,
        applyFilters,
    });

    const NON_STOP = 1;
    let initialRawFilters;

    function getProcessedFilters(rawFilters) {
        if (!rawFilters) {
            return {};
        }

        initialRawFilters = angular.copy(rawFilters);

        // eslint-disable-next-line new-cap
        const flightsFactory = new atcFFactory('atc');

        const {RecommendationSegmentTimeRanges,
            Airports,
            FlightStopsType,
            BaggageAllowance,
            Refundable,
            Changeable,
            TicketValidity,
            ConnectionAirports,
            MinimumPrice,
            MaximumPrice,
            MinimumDuration,
            MaximumDuration,
            MinimumStopover,
            MaximumStopover,
            FlightNumber,
        } = rawFilters;
        const sortedAirlines = sortAirlines(rawFilters.Airline);
        const processedAirports = processAirports(Airports);
        const durationRangeSlider = initRangeSlider(
            timeService.convertTimeToMilliseconds(MinimumDuration),
            timeService.convertTimeToMilliseconds(MaximumDuration));
        const stopoverRangeSlider = initRangeSlider(
            timeService.convertTimeToMilliseconds(MinimumStopover),
            timeService.convertTimeToMilliseconds(MaximumStopover));

        function extractValue({Value}) {
            return Value;
        }

        const exchangeValues = ({Key, Value}) => ({Key: Value, Value: Key});

        function getFlightStopsType() {
            const params = flightsFactory.params || {};
            const {DirectFlightsOnly: onlyDirect} = params;
            return onlyDirect
                ? (FlightStopsType || [])
                    .filter((item) => item.Value === NON_STOP)
                    .map(exchangeValues)
                : (FlightStopsType || []).map(exchangeValues);
        }
        const updateFilters = {
            flightStopsType: (FlightStopsType || []).map(extractValue),
            airlines: (sortedAirlines || []).map(({Key}) => Key),
            airports: (getReducedAirports || [])(processedAirports),
            connectionAirports: (ConnectionAirports || []).map(extractValue),
            baggageAllowance: (BaggageAllowance || []).map(extractValue),
            changeable: (Changeable || []).map(extractValue),
            refundable: (Refundable || []).map(extractValue),
            ticketValidity: (TicketValidity || []).map(extractValue),

            priceRangeSlider: initRangeSlider(MinimumPrice, MaximumPrice),
            departureRangeSliders: (RecommendationSegmentTimeRanges || []).map(({MinDepartureHour, MaxDepartureHour, DepartureCity, Index}) => Object.assign(initRangeSlider(MinDepartureHour, MaxDepartureHour), {DepartureCity, Index})),
            arrivalRangeSliders: (RecommendationSegmentTimeRanges || []).map(({MinArrivalHour, MaxArrivalHour, ArrivalCity, Index}) => Object.assign(initRangeSlider(MinArrivalHour, MaxArrivalHour), {ArrivalCity, Index})),
            durationRangeSlider,
            stopoverRangeSlider,
            flightNumber: FlightNumber,
        };
        const filters = {
            airlines: sortedAirlines,
            flightStopsType: getFlightStopsType(),
            airports: processedAirports,
            connectionAirports: (ConnectionAirports || []).map(({Key, Value}) => ({Key: Value, Value: `${Value}: ${Key}`})),
            baggageAllowance: (BaggageAllowance || []).map(exchangeValues),
            changeable: (Changeable || []).map(exchangeValues),
            refundable: (Refundable || []).map(exchangeValues),
            ticketValidity: (TicketValidity || []).map(exchangeValues),
            minimumDuration: timeService.convertTimeToMilliseconds(MinimumDuration),
            maximumDuration: timeService.convertTimeToMilliseconds(MaximumDuration),
            minimumStopover: timeService.convertTimeToMilliseconds(MinimumStopover),
            maximumStopover: timeService.convertTimeToMilliseconds(MaximumStopover),
            durationRangeSlider,
            stopoverRangeSlider,
            flightNumber: FlightNumber,
            priceRangeSlider: initRangeSlider(MinimumPrice, MaximumPrice),
        };
        return {
            updateFilters,
            filters,
        };
    }

    function getReducedAirports(airports) {
        return airports.reduce((allAirports, airport) => allAirports.concat(...airport.Airports.map(({Key}) => Key)), []);
    }

    function initRangeSlider(min, max) {
        return {
            model: min,
            range: max,
            floor: min,
            ceiling: max,
        };
    }

    function sortAirlines(airlines) {
        let keys = Object.keys(airlines);
        const result = [];
        keys = keys.sort((first, second) => (first.toLowerCase() > second.toLowerCase() ? 1 : -1));
        keys.forEach((item) => result.push({
            Value: item,
            Key: airlines[item],
        }));

        return result;
    }

    function processAirports(cityAirports) {
        cityAirports
            .forEach((city) => city.Airports = city.Airports
                .map(({Key, Value}) => ({Key: Value, Value: `${Value}: ${Key}`})));
        return cityAirports;
    }

    function applyFilters(appliedFilters) {
        // eslint-disable-next-line new-cap
        const flightsFactory = new atcFFactory('atc');

        const {comparisonFilter} = appliedFilters;

        function getTimeRanges(departure, arrival) {
            return departure.map((item, idx) => {
                return {
                    MinDepartureHour: item.floor,
                    MaxDepartureHour: item.ceiling,
                    MinArrivalHour: arrival[idx].floor,
                    MaxArrivalHour: arrival[idx].ceiling,
                    Index: item.Index,
                };
            });
        }

        function isLengthEqual(firstCollection, secondCollection) {
            return firstCollection.length === secondCollection.length;
        }

        const filters = {
            FlightStopsType: appliedFilters.flightStopsType,
            MinimumPrice: appliedFilters.priceRangeSlider.floor,
            MaximumPrice: appliedFilters.priceRangeSlider.ceiling,
            RecommendationSegmentTimeRanges: getTimeRanges(appliedFilters.departureRangeSliders, appliedFilters.arrivalRangeSliders),
            MinimumStopover: convertDuration(appliedFilters.stopoverRangeSlider.floor),
            MaximumStopover: convertDuration(appliedFilters.stopoverRangeSlider.ceiling),
            MinimumDuration: convertDuration(appliedFilters.durationRangeSlider.floor),
            MaximumDuration: convertDuration(appliedFilters.durationRangeSlider.ceiling),
            BaggageAllowance: appliedFilters.baggageAllowance,
            Refundable: appliedFilters.refundable,
            Changeable: appliedFilters.changeable,
            FlightNumber: appliedFilters.flightNumber,
        };
        Object.assign(filters, !isLengthEqual(initialRawFilters.TicketValidity || [], appliedFilters.ticketValidity || []) ? {TicketValidity: appliedFilters.ticketValidity} : null);
        Object.assign(filters, !isLengthEqual(initialRawFilters.ConnectionAirports || [], appliedFilters.connectionAirports || []) ? {ConnectionAirports: appliedFilters.connectionAirports} : null);
        Object.assign(filters, !isLengthEqual(Object.keys(initialRawFilters.Airline || {}), appliedFilters.airlines || {}) ? {Airline: appliedFilters.airlines} : null);
        Object.assign(filters, !isLengthEqual(getReducedAirports(processAirports(initialRawFilters.Airports || [])), appliedFilters.airports || []) ? {Airports: appliedFilters.airports} : null);
        flightsFactory.applyFilters(filters, comparisonFilter);
    }

    function convertDuration(milliseconds) {
        const duration = moment.duration(milliseconds);
        const hours = duration.hours() >= 10 ? `${duration.hours()}` : `0${duration.hours()}`;
        const minutes = duration.minutes() >= 10 ? `${duration.minutes()}` : `0${duration.minutes()}`;
        const seconds = duration.seconds() >= 10 ? `${duration.seconds()}` : `0${duration.seconds()}`;
        return `${duration.days()}.${hours}:${minutes}:${seconds}`;
    }
}



// WEBPACK FOOTER //
// ./src/app/modules/atc/atc-flights-view/filters/flight-filters-service.js